import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MessageSquare, 
  Send, 
  User, 
  Inbox, 
  SendHorizontal,
  Search,
  Plus,
  Mail,
  MailOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('inbox');
  const [contacts, setContacts] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({
    receiver_id: '',
    subject: '',
    content: ''
  });
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchConversation(selectedContact.id);
    }
  }, [selectedContact]);

  const fetchData = async () => {
    try {
      const [contactsRes, inboxRes, sentRes] = await Promise.all([
        api.get('/portal/contacts'),
        api.get('/messages/inbox'),
        api.get('/messages/sent')
      ]);
      setContacts(contactsRes.data.filter(c => c.id !== user?.id));
      setInbox(inboxRes.data);
      setSent(sentRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (userId) => {
    try {
      const res = await api.get(`/messages/conversation/${userId}`);
      setConversation(res.data);
    } catch (error) {
      console.error('Conversation fetch error:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    
    try {
      await api.post('/messages', {
        receiver_id: selectedContact.id,
        subject: language === 'ar' ? 'رسالة' : 'Message',
        content: newMessage
      });
      setNewMessage('');
      fetchConversation(selectedContact.id);
      fetchData();
      toast.success(language === 'ar' ? 'تم إرسال الرسالة' : 'Message sent');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الإرسال' : 'Failed to send');
    }
  };

  const sendComposedMessage = async () => {
    if (!composeData.receiver_id || !composeData.content) {
      toast.error(language === 'ar' ? 'أكمل جميع الحقول' : 'Fill all required fields');
      return;
    }

    try {
      await api.post('/messages', composeData);
      setComposeOpen(false);
      setComposeData({ receiver_id: '', subject: '', content: '' });
      fetchData();
      toast.success(language === 'ar' ? 'تم إرسال الرسالة' : 'Message sent');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الإرسال' : 'Failed to send');
    }
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      admin: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', label: language === 'ar' ? 'مدير' : 'Admin' },
      lawyer: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', label: language === 'ar' ? 'محامي' : 'Lawyer' },
      client: { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400', label: language === 'ar' ? 'عميل' : 'Client' },
      assistant: { color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400', label: language === 'ar' ? 'مساعد' : 'Assistant' }
    };
    const r = roleMap[role] || roleMap.client;
    return <Badge className={r.color}>{r.label}</Badge>;
  };

  const filteredContacts = contacts.filter(c => 
    c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = inbox.filter(m => m.status === 'unread').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="messages-page">
      <Header title={language === 'ar' ? 'الرسائل' : 'Messages'} />
      
      <div className="p-6">
        <Card className="h-[calc(100vh-180px)] flex flex-col">
          <CardHeader className="border-b flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                {language === 'ar' ? 'الرسائل' : 'Messages'}
              </CardTitle>
              {unreadCount > 0 && (
                <Badge variant="destructive">{unreadCount} {language === 'ar' ? 'جديدة' : 'new'}</Badge>
              )}
            </div>
            
            <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2">
                  <Plus className="w-4 h-4" />
                  {language === 'ar' ? 'رسالة جديدة' : 'New Message'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'ar' ? 'رسالة جديدة' : 'New Message'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{language === 'ar' ? 'إلى' : 'To'} *</label>
                    <Select 
                      value={composeData.receiver_id}
                      onValueChange={(v) => setComposeData({...composeData, receiver_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'ar' ? 'اختر المستلم' : 'Select recipient'} />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.full_name} ({c.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{language === 'ar' ? 'الموضوع' : 'Subject'}</label>
                    <Input
                      value={composeData.subject}
                      onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                      placeholder={language === 'ar' ? 'موضوع الرسالة' : 'Message subject'}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{language === 'ar' ? 'الرسالة' : 'Message'} *</label>
                    <Textarea
                      value={composeData.content}
                      onChange={(e) => setComposeData({...composeData, content: e.target.value})}
                      placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                      rows={4}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setComposeOpen(false)}>
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button className="btn-accent" onClick={sendComposedMessage}>
                      <Send className="w-4 h-4 mr-2" />
                      {language === 'ar' ? 'إرسال' : 'Send'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          
          <div className="flex flex-1 overflow-hidden">
            {/* Contacts Sidebar */}
            <div className="w-80 border-r flex flex-col">
              {/* Search */}
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {/* Contact List */}
              <ScrollArea className="flex-1">
                {filteredContacts.length > 0 ? (
                  filteredContacts.map(contact => {
                    const hasUnread = inbox.some(m => m.sender_id === contact.id && m.status === 'unread');
                    return (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContact(contact)}
                        className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                          selectedContact?.id === contact.id ? 'bg-muted' : ''
                        } ${hasUnread ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            {hasUnread && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm truncate">{contact.full_name}</p>
                              {getRoleBadge(contact.role)}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    {language === 'ar' ? 'لا توجد جهات اتصال' : 'No contacts'}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Conversation Area */}
            <div className="flex-1 flex flex-col">
              {selectedContact ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b bg-muted/30 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedContact.full_name}</p>
                        <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                      </div>
                    </div>
                    {getRoleBadge(selectedContact.role)}
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {conversation.length > 0 ? (
                        conversation.map(msg => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] p-3 rounded-lg ${
                                msg.sender_id === user?.id
                                  ? 'bg-[hsl(var(--accent))] text-white'
                                  : 'bg-muted'
                              }`}
                            >
                              {msg.subject && msg.subject !== 'رسالة' && msg.subject !== 'Message' && (
                                <p className={`text-xs font-medium mb-1 ${
                                  msg.sender_id === user?.id ? 'text-white/80' : 'text-muted-foreground'
                                }`}>
                                  {msg.subject}
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_id === user?.id ? 'text-white/70' : 'text-muted-foreground'
                              }`}>
                                {new Date(msg.created_at).toLocaleString(language === 'ar' ? 'ar-IQ' : 'en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>{language === 'ar' ? 'لا توجد رسائل بعد' : 'No messages yet'}</p>
                          <p className="text-sm">{language === 'ar' ? 'ابدأ المحادثة!' : 'Start the conversation!'}</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        className="flex-1"
                      />
                      <Button className="btn-accent" onClick={sendMessage}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">{language === 'ar' ? 'اختر محادثة' : 'Select a conversation'}</p>
                    <p className="text-sm">{language === 'ar' ? 'اختر جهة اتصال من القائمة للبدء' : 'Choose a contact from the list to start'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
